export const RESOURCES = ['brick', 'lumber', 'wool', 'grain', 'ore'] as const;
export type Resource = (typeof RESOURCES)[number];
export type Resources = Record<Resource, number>;

export const BANK_PER_RESOURCE = 19;

export function emptyResources(): Resources {
  return { brick: 0, lumber: 0, wool: 0, grain: 0, ore: 0 };
}

export function res(partial: Partial<Resources> = {}): Resources {
  return { ...emptyResources(), ...partial };
}

export function isResource(x: unknown): x is Resource {
  return typeof x === 'string' && (RESOURCES as readonly string[]).includes(x);
}

/** Full Resources object with every key a non-negative safe integer and no extras. */
export function isResources(x: unknown): x is Resources {
  if (typeof x !== 'object' || x === null || Array.isArray(x)) return false;
  const keys = Object.keys(x);
  if (keys.length !== RESOURCES.length) return false;
  for (const r of RESOURCES) {
    const v = (x as Record<string, unknown>)[r];
    if (typeof v !== 'number' || !Number.isSafeInteger(v) || v < 0) return false;
  }
  return true;
}

export function totalResources(r: Resources): number {
  return r.brick + r.lumber + r.wool + r.grain + r.ore;
}

export function hasResources(have: Resources, need: Resources): boolean {
  return RESOURCES.every((r) => have[r] >= need[r]);
}

export function addResources(target: Resources, delta: Resources): void {
  for (const r of RESOURCES) target[r] += delta[r];
}

export function subtractResources(target: Resources, delta: Resources): void {
  for (const r of RESOURCES) target[r] -= delta[r];
}

/** Move `amount` from one pool to another (hand ↔ bank ↔ hand). Caller checks sufficiency. */
export function transfer(from: Resources, to: Resources, amount: Resources): void {
  subtractResources(from, amount);
  addResources(to, amount);
}

export const COSTS = {
  road: res({ brick: 1, lumber: 1 }),
  settlement: res({ brick: 1, lumber: 1, wool: 1, grain: 1 }),
  city: res({ grain: 2, ore: 3 }),
  devCard: res({ wool: 1, grain: 1, ore: 1 }),
} as const satisfies Record<string, Resources>;
